/**
 * Offline Store — IndexedDB persistence layer for offline-capable PWA.
 *
 * Uses Dexie.js to cache:
 *  - Appraisal list (dashboard / appraisals page)
 *  - Individual appraisal njreap_data blobs (editor)
 *  - Billing & verification status (subscription gating)
 *  - Pending sync queue entries (offline writes)
 */
import Dexie, { type Table } from 'dexie';

// ── Cached record shapes ────────────────────────────────────────────

export interface CachedAppraisalListItem {
  id: string;
  userId: string;
  file_number: string;
  custom_file_number?: string | null;
  property_address: string;
  city: string | null;
  state: string | null;
  zip: string | null;
  status: string;
  priority: string | null;
  created_at: string;
  updated_at: string;
  due_date: string | null;
  report_category: string;
  report_engine: string;
  urar_report_id: string | null;
  client_name: string | null;
  fee: number | null;
  njreap_data: any;
}

export interface CachedAppraisalBlob {
  /** appraisal id */
  id: string;
  userId: string;
  /** Full row fetched by AppraisalEditor */
  data: any;
  /** ISO timestamp of when this was cached */
  cachedAt: string;
}

export interface CachedBillingStatus {
  userId: string;
  status: any; // BillingStatus minus `loading`
  cachedAt: string;
}

export interface CachedVerificationStatus {
  userId: string;
  status: any; // VerificationStatus minus `loading`
  cachedAt: string;
}

export interface SyncQueueEntry {
  /** Auto-incremented */
  queueId?: number;
  /** appraisal id */
  appraisalId: string;
  userId: string;
  /** The prepareSavePayload() result to PATCH */
  payload: any;
  /** ISO timestamp when the change was made locally */
  createdAt: string;
  /** Number of times we've attempted to flush this entry */
  attempts: number;
}

export interface PendingPhoto {
  /** Auto-incremented */
  id?: number;
  appraisalId: string;
  userId: string;
  teamId: string | null;
  /** Photo blob stored as ArrayBuffer for IndexedDB compatibility */
  blobData: ArrayBuffer;
  contentType: string;
  fileName: string;
  category: string;
  isCoverPhoto: boolean;
  capturedAt: string;
  /** Local blob URL for optimistic UI display */
  localUrl: string;
  /** ISO timestamp when queued */
  createdAt: string;
  attempts: number;
}

// ── Database ────────────────────────────────────────────────────────

class OfflineDatabase extends Dexie {
  appraisalList!: Table<CachedAppraisalListItem, string>;
  appraisalBlobs!: Table<CachedAppraisalBlob, string>;
  billingStatus!: Table<CachedBillingStatus, string>;
  verificationStatus!: Table<CachedVerificationStatus, string>;
  syncQueue!: Table<SyncQueueEntry, number>;
  pendingPhotos!: Table<PendingPhoto, number>;

  constructor() {
    super('AshlarOffline');

    this.version(1).stores({
      // Keyed by appraisal id; indexed by userId for per-user queries
      appraisalList: 'id, userId',
      // Keyed by appraisal id
      appraisalBlobs: 'id, userId',
      // Keyed by userId (one row per user)
      billingStatus: 'userId',
      verificationStatus: 'userId',
      // Auto-increment PK; indexed by appraisalId for dedup
      syncQueue: '++queueId, appraisalId, userId',
    });

    this.version(2).stores({
      appraisalList: 'id, userId',
      appraisalBlobs: 'id, userId',
      billingStatus: 'userId',
      verificationStatus: 'userId',
      syncQueue: '++queueId, appraisalId, userId',
      pendingPhotos: '++id, appraisalId, userId',
    });
  }
}

export const offlineDb = new OfflineDatabase();

// ── Appraisal list cache ────────────────────────────────────────────

export async function cacheAppraisalList(userId: string, items: any[]) {
  // Clear stale entries for this user, then bulk-put new ones
  await offlineDb.appraisalList.where('userId').equals(userId).delete();
  const rows: CachedAppraisalListItem[] = items.map((item) => ({
    ...item,
    userId,
  }));
  await offlineDb.appraisalList.bulkPut(rows);
}

export async function getCachedAppraisalList(userId: string): Promise<CachedAppraisalListItem[]> {
  return offlineDb.appraisalList.where('userId').equals(userId).toArray();
}

// ── Individual appraisal blob cache ─────────────────────────────────

export async function cacheAppraisalBlob(userId: string, appraisalId: string, data: any) {
  await offlineDb.appraisalBlobs.put({
    id: appraisalId,
    userId,
    data,
    cachedAt: new Date().toISOString(),
  });
}

export async function getCachedAppraisalBlob(appraisalId: string): Promise<CachedAppraisalBlob | undefined> {
  return offlineDb.appraisalBlobs.get(appraisalId);
}

// ── Billing status cache ────────────────────────────────────────────

export async function cacheBillingStatus(userId: string, status: any) {
  await offlineDb.billingStatus.put({
    userId,
    status,
    cachedAt: new Date().toISOString(),
  });
}

export async function getCachedBillingStatus(userId: string): Promise<CachedBillingStatus | undefined> {
  return offlineDb.billingStatus.get(userId);
}

// ── Verification status cache ───────────────────────────────────────

export async function cacheVerificationStatus(userId: string, status: any) {
  await offlineDb.verificationStatus.put({
    userId,
    status,
    cachedAt: new Date().toISOString(),
  });
}

export async function getCachedVerificationStatus(userId: string): Promise<CachedVerificationStatus | undefined> {
  return offlineDb.verificationStatus.get(userId);
}

// ── Sync queue ──────────────────────────────────────────────────────

export async function enqueueSyncWrite(
  appraisalId: string,
  userId: string,
  payload: any,
) {
  // Replace any existing queued write for the same appraisal (last-write-wins locally)
  await offlineDb.syncQueue.where('appraisalId').equals(appraisalId).delete();
  await offlineDb.syncQueue.add({
    appraisalId,
    userId,
    payload,
    createdAt: new Date().toISOString(),
    attempts: 0,
  });
}

export async function getPendingSyncEntries(userId: string): Promise<SyncQueueEntry[]> {
  return offlineDb.syncQueue.where('userId').equals(userId).toArray();
}

export async function removeSyncEntry(queueId: number) {
  await offlineDb.syncQueue.delete(queueId);
}

export async function incrementSyncAttempts(queueId: number) {
  await offlineDb.syncQueue.update(queueId, { attempts: (await offlineDb.syncQueue.get(queueId))?.attempts ?? 0 + 1 });
}

export async function getPendingSyncCount(userId: string): Promise<number> {
  return offlineDb.syncQueue.where('userId').equals(userId).count();
}

// ── Pending photos queue ───────────────────────────────────────────

export async function enqueuePhotoUpload(photo: Omit<PendingPhoto, 'id'>) {
  await offlineDb.pendingPhotos.add(photo as PendingPhoto);
}

export async function getPendingPhotos(userId: string): Promise<PendingPhoto[]> {
  return offlineDb.pendingPhotos.where('userId').equals(userId).toArray();
}

export async function removePendingPhoto(id: number) {
  await offlineDb.pendingPhotos.delete(id);
}

export async function getPendingPhotoCount(userId: string): Promise<number> {
  return offlineDb.pendingPhotos.where('userId').equals(userId).count();
}
